import {cloneDeep} from 'app/core/helpers/object';
import {SortDirection} from 'app/core/interfaces/common.interface';
import {Observable, of, timer} from 'rxjs';
import {map, shareReplay, switchMap} from 'rxjs/operators';
import {DataAnonymizationMode, Dimension, FilterInput, FunnelFilterDimension, FunnelFilterInput} from '../../../../globalTypes';
import {FilterValue, Group, ReportFilters, SelectedMeasure, TimeGroup} from 'app/features/analytics/service/filter-value.interface';
import {isTimeDimension} from 'app/features/analytics/shared/utils';
import {
    RichStats_analytics_richStats,
    RichStats_analytics_richStats_stats_AppInfo,
    RichStats_analytics_richStats_stats_StatsValue,
} from 'app/features/analytics/service/gql/RichStats';
import {filterValueToFilterInput} from 'app/features/analytics/service/filter-options.helpers';
import {ReportsMode} from 'app/features/analytics/shared/components/analytics-table/analytics-table.helpers';
import {StatsResult, StatsValue, Totals} from 'app/features/analytics/service/stats.service.interface';
import {AppSplit} from 'app/features/analytics/features/reports/dimension';

export function ejectSubCategories(rows: StatsValue[][], groupByIndex: number): {id: string; value: string}[] | null {
    if (groupByIndex === -1) {
        return null;
    }
    const categories = new Map<string, RichStats_analytics_richStats_stats_StatsValue>();

    for (let i = 0; i < rows.length; i++) {
        const value = rows[i][groupByIndex] as {id?: string; value: string};
        categories.set(value.id || value.value, value as RichStats_analytics_richStats_stats_StatsValue);
    }
    const result = [...categories.values()];
    // @ts-ignore
    const othersIndex = result.findIndex(v => v.id === 'Others' || v === 'Others');
    if (othersIndex !== -1) {
        const others = result[othersIndex];
        result.splice(othersIndex, 1);
        result.push(others);
    }
    return result;
}

export const toPlainStatValue = (value: string): RichStats_analytics_richStats_stats_StatsValue => ({
    value,
    id: null,
    __typename: 'StatsValue',
});

function extractNormalisedMeasuresFromStats(
    measuresToFetch: SelectedMeasure[],
    originalFilters: ReportFilters,
    {stats, totals, anonDict}: StatsResult
): StatsResult {
    const groupByColumns = originalFilters.groupBy;
    const measuresColumns: number[] = originalFilters.measures.map(
        (m, i) => groupByColumns.length + measuresToFetch.findIndex(v => m.id === v.id && m.day === v.day)
    );

    return {
        filters: originalFilters,
        anonDict: anonDict ? anonDict : [],
        stats: stats.map(line => [
            ...groupByColumns.map((_, i) => line[i]),
            ...originalFilters.measures.map((_, i) => line[measuresColumns[i]]),
        ]),
        totals,
    };
}

export const restoreColumnsOrderAndNormalize = (
    originalFilters: ReportFilters,
    requestFilters: ReportFilters,
    data: RichStats_analytics_richStats
): StatsResult => {
    const originalOrder = [...originalFilters.groupBy, ...originalFilters.measures] as {id: string; day?: number}[];

    const requestOrder = [...requestFilters.groupBy, ...requestFilters.measures] as {id: string; day?: number}[];

    const columnMap = new Map();
    requestOrder.forEach((requestValue, i) => {
        columnMap.set(
            originalOrder.findIndex(originalValue => originalValue.id === requestValue.id && originalValue.day === requestValue.day),
            i
        );
    });

    return {
        filters: originalFilters,
        anonDict: 'anonDict' in data ? data.anonDict : [],
        stats: (data.stats as StatsValue[][]).map(line =>
            line
                .map((_, i) => line[columnMap.get(i)])
                .map((v, i) => {
                    switch (v.__typename) {
                        case 'UaAdSet':
                            return {...v, value: v.name};
                        case 'UaCampaign':
                            return {...v, value: v.campaignName};
                        case 'SegmentInfo':
                            return {...v, value: v.name};
                        case 'AppInfo':
                            return {...v, value: v.name};
                        case 'LineItemInfo':
                            return {...v, value: v.value};
                        case 'SourceAppInfo':
                            return {...v, value: v.id};
                        case 'StatsValue':
                            return !v.id ? {...v, id: v.value} : v;
                        default:
                            return v;
                    }
                })
        ),
        totals: originalFilters.measures
            .map(e => {
                if (data.totals.some(m => m.measure === e.id && m.day === e.day)) {
                    return {
                        measure: e,
                        value: data.totals.find(m => m.measure === e.id && m.day === e.day).value,
                    } as Totals;
                }
            })
            .filter(v => Boolean(v && v.value)),
    };
};

export interface BatchSort {
    measure: SelectedMeasure;
    order: SortDirection;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface BatchState<T extends StatsResult> {
    sortBy?: BatchSort;
    result: Observable<{data: StatsResult; measuresToFetch: SelectedMeasure[]}>;
    measures: SelectedMeasure[];
}

export class BatchService {
    newBatchGroup<T extends StatsResult>(batchInterval?: number): Map<string, BatchState<T>> & {timer?: number} {
        const batch = new Map<string, BatchState<T>>();
        (batch as any).timer = batchInterval;
        return batch;
    }

    private mergeMeasures(...args: SelectedMeasure[][]): SelectedMeasure[] {
        return [
            ...args
                .reduce((acc, list) => {
                    list.forEach(m => acc.set(m.id + m.day, m));
                    return acc;
                }, new Map())
                .values(),
        ];
    }

    // this should work only with measures of one kind
    // batch for stats or for UA stats
    // NOT Both
    batchRequest<T extends StatsResult>(
        batches: Map<string, BatchState<T>> & {timer?: number},
        reportFilters: ReportFilters,
        dataFetcher: (v: ReportFilters) => Observable<T>,
        batchSortBy?: BatchSort
    ): Observable<StatsResult> {
        reportFilters = cloneDeep(reportFilters);
        const key = JSON.stringify([reportFilters.filter, reportFilters.groupBy, reportFilters.topFilter]);

        if (reportFilters.measures.length === 0) {
            // no measures nothing to batch
            batches = this.newBatchGroup(0);
        }

        if (batches.has(key)) {
            if (batchSortBy && !batches.get(key).sortBy) {
                batches.get(key).sortBy = batchSortBy;
            }
            batches.get(key).measures = this.mergeMeasures(reportFilters.measures, batches.get(key).measures);
        } else {
            batches.set(key, {
                sortBy: batchSortBy,
                result: (batches.timer ? timer(batches.timer) : of(null)).pipe(
                    map(() => {
                        const measuresToFetch = [...batches.get(key).measures];
                        const sortBy = batches.get(key).sortBy;
                        batches.delete(key);
                        return {measuresToFetch, sortBy};
                    }),
                    switchMap(({measuresToFetch, sortBy}) =>
                        dataFetcher({
                            ...reportFilters,
                            measures: measuresToFetch,
                        }).pipe(
                            map(data =>
                                sortBy
                                    ? {measuresToFetch, data: sortStatsResult(measuresToFetch, reportFilters, sortBy, data)}
                                    : {measuresToFetch, data}
                            )
                        )
                    ),

                    shareReplay(1)
                ),
                measures: [...reportFilters.measures],
            });
        }

        return batches
            .get(key)
            .result.pipe(
                map(({data, measuresToFetch}) =>
                    batches.timer ? extractNormalisedMeasuresFromStats(measuresToFetch, reportFilters, data) : data
                )
            );
    }
}

const sortStatsResult = <T extends StatsResult>(
    measuresToFetch: SelectedMeasure[],
    filters: ReportFilters,
    sortBy: BatchSort,
    data: T
): T => {
    if (!data.stats.length) {
        return data;
    }

    const dateIndex = filters.groupBy.findIndex(g => isTimeDimension(g.id));
    let fieldIndex = measuresToFetch.findIndex(v => v.id === sortBy.measure.id && v.day === sortBy.measure.day);
    if (dateIndex === -1 || fieldIndex === -1) {
        console.warn(`Could not found date or ${sortBy.measure.label} index!`);
        return data;
    }
    fieldIndex += filters.groupBy.length;

    const result = data.stats.slice(0).sort((a, b) => {
        if (a[dateIndex].value !== b[dateIndex].value) {
            return a[dateIndex].value > b[dateIndex].value ? 1 : -1;
        }
        if (a[fieldIndex].value === b[fieldIndex].value) {
            return 0;
        }
        return Number(a[fieldIndex].value) < Number(b[fieldIndex].value) ? 1 : -1;
    });

    return {
        ...data,
        stats: result,
    };
};

export function replaceDateWithInstallDate(reportFilters: ReportFilters): ReportFilters {
    let filters;
    let groupBy;
    if (reportFilters.filter.some(f => f.id === Dimension.DATE)) {
        filters = reportFilters.filter.map(f => (f.id === Dimension.DATE ? {...f, id: Dimension.INSTALL_DATE} : f));
    }
    if (reportFilters.groupBy.some(f => f.id === Dimension.DATE)) {
        groupBy = reportFilters.groupBy.map(g => (g.id === Dimension.DATE ? {...g, id: Dimension.INSTALL_DATE} : g));
    }
    if (filters || groupBy) {
        return {
            ...reportFilters,
            filter: filters || reportFilters.filter,
            groupBy: groupBy || reportFilters.groupBy,
        };
    }

    return reportFilters;
}

export function getAppTextValue(appInfo: RichStats_analytics_richStats_stats_AppInfo, appSplit: AppSplit): string {
    switch (appSplit) {
        case AppSplit.BundleId:
            return appInfo.bundleId;
        case AppSplit.ID:
            return appInfo.id;
        default:
            return appInfo.name;
    }
}

export function setTextToClipboard(text): Promise<void> {
    return navigator.clipboard.writeText(text);
}

export function mapGroupBy(group: Group) {
    if (!group) {
        return;
    }

    if (isTimeDimension(group.id)) {
        return {dimension: group.id as Dimension, timeBucket: (group as TimeGroup).timeBucket};
    }

    return {dimension: group.id as Dimension};
}

export function excludeDateDimensionsAndMapFilters(filters: FilterValue[]): FilterInput[] {
    return filters
        .filter(filter => !isTimeDimension(filter.id))
        .filter(filter => (filter.value && filter.value.length > 0) || filter.search)
        .map(filter => filterValueToFilterInput(filter));
}

export function filterFunnelFilterInputs(filters: FilterInput[]): FunnelFilterInput[] {
    const funnelDM = Object.values(FunnelFilterDimension);
    // @ts-ignore
    return filters.filter((v: FunnelFilterDimension) => funnelDM.includes(v.dimension as any)) as FunnelFilterInput[];
}

export function isAnonymizeMode(mode: ReportsMode): DataAnonymizationMode {
    switch (mode) {
        case ReportsMode.Demo:
            return DataAnonymizationMode.ON;
        case ReportsMode.DemoEdit:
            return DataAnonymizationMode.EDIT;
        default:
            return DataAnonymizationMode.OFF;
    }
}

export function splitFilters(reportFilters: ReportFilters, condition: (m: SelectedMeasure) => 0 | 1): [ReportFilters, ReportFilters] {
    const acc = new Map<0 | 1, SelectedMeasure[]>([
        [0, []],
        [1, []],
    ]);
    reportFilters.measures.forEach(m => {
        const index = condition(m);
        acc.get(index).push(m);
    });
    return [
        {...reportFilters, measures: acc.get(0)},
        {...reportFilters, measures: acc.get(1)},
    ];
}
